import { trackEvent } from '@/utils/analytics';
import { useCallback, useEffect } from 'react';
import { useIDEState } from './use-ide-state';

export const useKeyboardShortcuts = () => {
    const { state, closeTab, closeAllTabs, toggleFileExplorer, toggleOutputPanel, addOutputMessage, toggleSearch } =
        useIDEState();

    // We'll need to access the About modal state from the MenuBar
    // For now, we'll use a custom event to trigger it
    const triggerAboutModal = useCallback(() => {
        const event = new CustomEvent('open-about-modal');
        document.dispatchEvent(event);
    }, []);

    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (state.modals.isAnyModalOpen || state.search.isOpen) return;
            // Don't trigger shortcuts when typing in input fields
            if (
                event.target instanceof HTMLInputElement ||
                event.target instanceof HTMLTextAreaElement ||
                (event.target as HTMLElement).contentEditable === 'true'
            ) {
                return;
            }

            const key = event.key.toLowerCase();
            const ctrl = event.ctrlKey || event.metaKey; // Support both Ctrl and Cmd
            const shift = event.shiftKey;
            const alt = event.altKey;

            // Close Tab: Ctrl+Alt+W
            if (ctrl && alt && key === 'w' && !shift) {
                event.preventDefault();
                if (state.activeTabId) {
                    closeTab(state.activeTabId);
                    addOutputMessage(`Closed tab via keyboard shortcut: ${state.activeTabId}`, 'debug');
                    trackEvent('keyboard_shortcut_used', {
                        shortcut: 'Ctrl+Alt+W',
                        action: 'close_tab',
                    });
                }
                return;
            }

            // Close All Tabs: Ctrl+Alt+Shift+W
            if (ctrl && alt && shift && key === 'w') {
                event.preventDefault();
                closeAllTabs();
                addOutputMessage('Closed all tabs via keyboard shortcut', 'debug');
                trackEvent('keyboard_shortcut_used', {
                    shortcut: 'Ctrl+Alt+Shift+W',
                    action: 'close_all_tabs',
                });
                return;
            }

            // Toggle File Explorer: Ctrl+Alt+E
            if (ctrl && alt && (key === 'e' || key === 'é') && !shift) {
                event.preventDefault();
                toggleFileExplorer();
                trackEvent('keyboard_shortcut_used', {
                    shortcut: 'Ctrl+Alt+E',
                    action: 'toggle_file_explorer',
                });
                return;
            }

            // Toggle Output Panel: Ctrl+Alt+J
            if (ctrl && alt && key === 'j' && !shift) {
                event.preventDefault();
                toggleOutputPanel();
                trackEvent('keyboard_shortcut_used', {
                    shortcut: 'Ctrl+Alt+J',
                    action: 'toggle_output_panel',
                });
                return;
            }

            // Open About: Ctrl+Shift+A
            if (ctrl && shift && key === 'a' && !alt) {
                event.preventDefault();
                triggerAboutModal();
                trackEvent('keyboard_shortcut_used', {
                    shortcut: 'Ctrl+Shift+A',
                    action: 'open_about_modal',
                });
                return;
            }

            // Toggle Search: Ctrl+Shift+F
            if (ctrl && shift && key === 'f' && !alt) {
                event.preventDefault();
                toggleSearch();
                addOutputMessage('Toggled file search via keyboard shortcut', 'debug');
                trackEvent('keyboard_shortcut_used', {
                    shortcut: 'Ctrl+Shift+F',
                    action: 'toggle_search',
                });
                return;
            }
        },
        [
            state.activeTabId,
            state.modals.isAnyModalOpen,
            state.search.isOpen,
            state.outputPanel.isVisible,
            closeTab,
            closeAllTabs,
            toggleFileExplorer,
            toggleOutputPanel,
            addOutputMessage,
            triggerAboutModal,
            toggleSearch,
        ]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);
};
